import React, { useState } from "react";
import "../../../../style/buttons.scss";

export type EAButtonAnimation = "addToBasket";

interface IProps {
  title: string | React.ReactNode;
  variant?: "filled" | "outlined" | "text" | "caption";
  color?: "primary" | "secondary" | "error" | "dark";
  onClick?: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  inputType?: "button" | "submit" | "reset" | undefined;
  loading?: boolean;
  loadingText?: string;
  noMargins?: boolean;
  compact?: boolean;
  /** Gdy podane (i przycisk nie jest disabled/loading), renderuje się jako <a href>
   * zamiast <button> — prawdziwa nawigacja przeglądarki, niezależna od window.open/JS,
   * której popup-blockery nie blokują. onClick nadal się wykonuje (np. dodatkowy window.open). */
  href?: string;
  target?: string;
  rel?: string;
  /** Krótka animacja po kliknięciu. `addToBasket` — ikona podskakuje, nad przyciskiem wylatuje „+”. */
  animationType?: EAButtonAnimation;
}

const EAButton: React.FC<IProps> = ({
  title,
  variant = "filled",
  color = "primary",
  onClick = () => {},
  startIcon,
  endIcon,
  fullWidth = false,
  disabled = false,
  inputType = "button",
  loading = false,
  loadingText = "Ładowanie...",
  noMargins = false,
  compact = false,
  href,
  target,
  rel,
  animationType,
}) => {
  const [popTick, setPopTick] = useState(0);
  const canPop = animationType === "addToBasket" && !disabled && !loading;
  const isPopping = canPop && popTick > 0;
  const popEndIcon = isPopping && Boolean(endIcon);
  const popStartIcon = isPopping && !endIcon && Boolean(startIcon);

  const className = `button button--${variant} button--${color} ${fullWidth ? "button--fullWidth" : ""} ${
    noMargins ? "button--no-margins" : ""
  } ${compact ? "button--compact" : ""} ${animationType === "addToBasket" ? "button--pop" : ""}`;

  const handleClick = () => {
    if (canPop) setPopTick((tick) => tick + 1);
    onClick();
  };

  const content = (
    <>
      {animationType === "addToBasket" && popTick > 0 && (
        <span key={`plus-${popTick}`} className="button__plus" aria-hidden="true">
          +
        </span>
      )}
      {loading ? (
        <span className="button__text button-text">{loadingText}</span>
      ) : (
        <>
          {startIcon && (
            <span
              key={popStartIcon ? `start-${popTick}` : "start"}
              className={`button__icon button__icon--start${popStartIcon ? " is-popping" : ""}`}
            >
              {startIcon}
            </span>
          )}
          <span className="button__text button-text">{title}</span>
          {endIcon && (
            <span
              key={popEndIcon ? `end-${popTick}` : "end"}
              className={`button__icon button__icon--end${popEndIcon ? " is-popping" : ""}`}
            >
              {endIcon}
            </span>
          )}
        </>
      )}
    </>
  );

  if (href && !disabled && !loading) {
    return (
      <a className={className} href={href} target={target} rel={rel} onClick={handleClick}>
        {content}
      </a>
    );
  }

  return (
    <button className={className} onClick={handleClick} disabled={disabled || loading} type={inputType}>
      {content}
    </button>
  );
};

export default EAButton;
